import { useEffect, useRef, useState } from 'react';
import { Tab, TabList, Tabs } from '@rynora/ui';

/**
 * CSS-only product showcase under the hero. Three rotating panels
 * (authoring canvas, optional AI assist, run / advisory confidence).
 * Maps to Quality Intelligence messaging — AI is optional for the spine.
 *
 * If /media/hero-flow-run.webm exists, switches to video automatically.
 */
type Panel = 'build' | 'generate' | 'run';

const PANEL_ORDER: Panel[] = ['build', 'generate', 'run'];
const PANEL_LABELS: Record<Panel, string> = {
  build: 'Author flow',
  generate: 'AI assist (optional)',
  run: 'Run & confidence',
};
const PANEL_INTERVAL_MS = 6000;
const HERO_VIDEO_SRC = '/media/hero-flow-run.webm';
const HERO_VIDEO_FALLBACK = '/media/hero-flow-run.mp4';
const HERO_POSTER = '/media/hero-poster.jpg';

interface ProductDemoProps {
  /** Optional caption rendered under the demo. */
  caption?: string;
}

export function ProductDemo({
  caption = 'Author → run → advisory confidence. AI assists authoring; the quality spine does not require an LLM.',
}: ProductDemoProps) {
  const [active, setActive] = useState<Panel>('build');
  const [videoReady, setVideoReady] = useState<'unknown' | 'available' | 'missing'>('unknown');
  const tickRef = useRef<number | null>(null);

  // Probe for a real demo video at /media/hero-flow-run.webm. If it's
  // there, hide the animated panels and render the video instead. The
  // probe uses HEAD so we never download the full file just to test
  // existence; if the server doesn't allow HEAD we fall back to a tiny
  // GET that bails on the first byte.
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const res = await fetch(HERO_VIDEO_SRC, { method: 'HEAD', cache: 'no-cache' });
        if (cancelled) return;
        setVideoReady(res.ok ? 'available' : 'missing');
      } catch {
        if (!cancelled) setVideoReady('missing');
      }
    };
    probe();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-rotate the animated panels. Pauses when the user hovers (the
  // ticker is paused in CSS via :hover; we just keep the JS rotation
  // alive so re-entry resumes from where the user left off).
  useEffect(() => {
    if (videoReady === 'available') return; // video covers the surface
    tickRef.current = window.setInterval(() => {
      setActive((prev) => {
        const idx = PANEL_ORDER.indexOf(prev);
        return PANEL_ORDER[(idx + 1) % PANEL_ORDER.length];
      });
    }, PANEL_INTERVAL_MS);
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, [videoReady]);

  if (videoReady === 'available') {
    return (
      <figure className="product-demo product-demo--video">
        <video
          className="product-demo__video"
          src={HERO_VIDEO_SRC}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={HERO_VIDEO_SRC} type="video/webm" />
          <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
        </video>
        {caption && <figcaption className="product-demo__caption">{caption}</figcaption>}
      </figure>
    );
  }

  return (
    <figure className="product-demo">
      {/* `pending-shared-ui-primitives` v0.3.2 — tablist migrated to the
          shared `<Tabs>` primitive in controlled mode. Existing
          `.product-demo__tabs` / `.product-demo__tab` / `.product-demo__tab-num`
          classes preserve the pill visual + numbered chip exactly.
          aria-controls is overridden to point at the existing
          `.product-demo__stage` element which already holds all three
          panels (each with their own `role="tabpanel"`). */}
      <Tabs value={active} onValueChange={(next) => setActive(next as Panel)} className="product-demo__tabs-root">
        <TabList className="product-demo__tabs" aria-label="Product demo">
          {PANEL_ORDER.map((id) => (
            <Tab
              key={id}
              value={id}
              aria-controls="product-demo-stage"
              className={`product-demo__tab${active === id ? ' is-active' : ''}`}
            >
              <span className="product-demo__tab-num">{PANEL_ORDER.indexOf(id) + 1}</span>
              {PANEL_LABELS[id]}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <div id="product-demo-stage" className="product-demo__stage" data-active={active}>
        <BuildPanel active={active === 'build'} />
        <GeneratePanel active={active === 'generate'} />
        <RunPanel active={active === 'run'} />
      </div>

      {caption && <figcaption className="product-demo__caption">{caption}</figcaption>}
    </figure>
  );
}

// ============================================================
// Panel 1: flow-builder canvas with nodes + edges + new-step pulse
// ============================================================

function BuildPanel({ active }: { active: boolean }) {
  return (
    <div className={`pd-panel${active ? ' is-active' : ''}`} role="tabpanel" aria-hidden={!active}>
      <div className="pd-canvas">
        <svg className="pd-canvas__edges" viewBox="0 0 600 280" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="pd-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.9" />
            </linearGradient>
          </defs>
          <path d="M 110 70 C 180 70, 200 145, 280 145" stroke="url(#pd-edge)" strokeWidth="2" fill="none" />
          <path d="M 380 145 C 460 145, 480 70, 540 70" stroke="url(#pd-edge)" strokeWidth="2" fill="none" />
          <path d="M 110 220 C 180 220, 200 145, 280 145" stroke="url(#pd-edge)" strokeWidth="2" fill="none" />
          <path d="M 380 145 C 460 145, 480 220, 540 220" stroke="url(#pd-edge)" strokeWidth="2" fill="none" />
        </svg>

        <div className="pd-node pd-node--start" style={{ top: '50px', left: '20px' }}>
          <span className="pd-node__dot pd-node__dot--start" />
          <div>
            <strong>Open</strong>
            <small>/checkout</small>
          </div>
        </div>

        <div className="pd-node" style={{ top: '200px', left: '20px' }}>
          <span className="pd-node__dot" />
          <div>
            <strong>Login</strong>
            <small>customer@acme.io</small>
          </div>
        </div>

        <div className="pd-node pd-node--center" style={{ top: '125px', left: '230px' }}>
          <span className="pd-node__dot pd-node__dot--ai" />
          <div>
            <strong>Apply coupon</strong>
            <small>SAVE10 · self-healed</small>
          </div>
        </div>

        <div className="pd-node" style={{ top: '50px', left: '440px' }}>
          <span className="pd-node__dot" />
          <div>
            <strong>Submit</strong>
            <small>Place order</small>
          </div>
        </div>

        <div className="pd-node pd-node--new" style={{ top: '200px', left: '440px' }}>
          <span className="pd-node__dot pd-node__dot--ok" />
          <div>
            <strong>Verify total</strong>
            <small>Just added</small>
          </div>
        </div>

        <div className="pd-add-pill" aria-hidden="true">
          + step
        </div>
      </div>
      <p className="pd-panel__caption">
        Build journeys visually. Locators may still exist under the hood — healing and lasting changes stay
        human-approved.
      </p>
    </div>
  );
}

// ============================================================
// Panel 2: typewriter prompt → step cards materialising
// ============================================================

const GENERATE_PROMPT = 'A returning customer applies coupon SAVE10 and checks out';
const GENERATE_STEPS = [
  'Open https://shop.acme.io',
  'Sign in as customer@acme.io',
  'Add 2 × T-Shirt M to cart',
  'Apply coupon SAVE10',
  'Verify discount = $5.00',
  'Submit order, expect /thank-you',
];

function GeneratePanel({ active }: { active: boolean }) {
  return (
    <div className={`pd-panel${active ? ' is-active' : ''}`} role="tabpanel" aria-hidden={!active}>
      <div className="pd-prompt">
        <span className="pd-prompt__label">Describe</span>
        <span className="pd-prompt__text">
          <span className="pd-prompt__typed" data-active={active}>
            {GENERATE_PROMPT}
          </span>
          <span className="pd-prompt__caret" aria-hidden="true" />
        </span>
      </div>

      <ol className="pd-steps">
        {GENERATE_STEPS.map((s, i) => (
          <li key={i} className="pd-steps__item" style={{ animationDelay: `${1.6 + i * 0.35}s` }}>
            <span className="pd-steps__num">{String(i + 1).padStart(2, '0')}</span>
            <span className="pd-steps__text">{s}</span>
            <span className="pd-steps__tag">AI</span>
          </li>
        ))}
      </ol>

      <p className="pd-panel__caption">
        Optional AI assist for authoring. Review steps before publish — AI does not gate execution or Release
        Confidence.
      </p>
    </div>
  );
}

// ============================================================
// Panel 3: live run report — checks ticking, progress bar filling
// ============================================================

const RUN_CASES = [
  { name: 'Open /checkout', dur: 320 },
  { name: 'Login as customer@acme.io', dur: 540 },
  { name: 'Add 2 × T-Shirt M to cart', dur: 410 },
  { name: 'Apply coupon SAVE10', dur: 380 },
  { name: 'Verify discount = $5.00', dur: 220 },
  { name: 'Submit order', dur: 470 },
];

function RunPanel({ active }: { active: boolean }) {
  return (
    <div className={`pd-panel${active ? ' is-active' : ''}`} role="tabpanel" aria-hidden={!active}>
      <div className="pd-run">
        <div className="pd-run__head">
          <span className="pd-run__title">checkout-with-coupon</span>
          <span className="pd-run__meta">QA · Chrome 124</span>
          <span className="pd-run__pill pd-run__pill--passing">passing</span>
        </div>

        <div className="pd-run__bar">
          <span className="pd-run__bar-fill" data-active={active} />
        </div>

        <ul className="pd-run__list">
          {RUN_CASES.map((c, i) => (
            <li key={c.name} className="pd-run__case" style={{ animationDelay: `${0.4 + i * 0.55}s` }}>
              <span className="pd-run__check" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="pd-run__name">{c.name}</span>
              <span className="pd-run__dur">{c.dur} ms</span>
            </li>
          ))}
        </ul>

        <div className="pd-run__footer">
          <span>6 / 6 steps</span>
          <span className="pd-run__divider">·</span>
          <span>2.34 s</span>
          <span className="pd-run__divider">·</span>
          <span>0 flaky</span>
        </div>
      </div>
      <p className="pd-panel__caption">
        Deterministic execution feeds Failure Intelligence and advisory Release Confidence — without inventing ROI or
        auto-blocking release.
      </p>
    </div>
  );
}
